import React, { useEffect, useMemo, useRef, useState } from "react";

function useMeasure(ref) {
  const [box, setBox] = useState({ width: 0, height: 0 });
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const cr = e.contentRect;
        setBox({ width: cr.width, height: cr.height });
      }
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return box;
}

function centerOrder(n) {
  if (n <= 0) return [];
  const order = [];
  let left = Math.floor((n - 1) / 2);
  let right = left + 1;
  order.push(left);
  while (order.length < n) {
    if (right < n) order.push(right++);
    if (left - 1 >= 0) order.push(--left);
  }
  return order;
}

function pickBestGrid(total, width, height, gap) {
  if (total <= 0 || width <= 0 || height <= 0) {
    return { cols: 0, rows: 0, cell: 0 };
  }
  const aspect = width / height;
  const idealCols = Math.max(1, Math.floor(Math.sqrt(total * aspect)));

  let best = { cols: 1, rows: total, cell: 1 };
  const tryCols = (cand) => {
    const rows = Math.max(1, Math.ceil(total / cand));
    const cellW = Math.floor((width - (cand - 1) * gap) / cand);
    const cellH = Math.floor((height - (rows - 1) * gap) / rows);
    const cell = Math.max(0, Math.min(cellW, cellH));
    if (cell > best.cell) best = { cols: cand, rows, cell };
  };

  const minCand = Math.max(1, idealCols - 80);
  const maxCand = idealCols + 80;
  for (let c = minCand; c <= maxCand; c++) tryCols(c);
  for (let c = 1; c <= Math.min(total, 32); c *= 2) tryCols(c);

  return best;
}

function computeFilledSet(total, filled, cols) {
  const rows = Math.ceil(total / cols);
  const clampedFilled = Math.max(0, Math.min(filled, total));
  let remaining = clampedFilled;
  const set = new Set();

  for (let r = 0; r < rows && remaining > 0; r++) {
    const rowStart = r * cols;
    const rowLen = Math.min(cols, total - rowStart);
    const order = centerOrder(rowLen);
    const take = Math.min(remaining, rowLen);
    for (let i = 0; i < take; i++) {
      set.add(rowStart + order[i]);
    }
    remaining -= take;
  }
  return set;
}

export default function DynamicWaffleChart({
  total = 0,
  filled = 0,
  colors = {
    filled: "#009000",
    empty: "#FF000066",
    gridBg: "#ffffff",
    border: "#ffffff",
  },
  gap = 1,
  rounded = true,
  className = "",
  style = {},
}) {
  const containerRef = useRef(null);
  const box = useMeasure(containerRef);

  const { cols, rows, cell } = useMemo(() => {
    return pickBestGrid(Math.max(0, total | 0), box.width, box.height || 1, gap);
  }, [total, box.width, box.height, gap]);

  const filledSet = useMemo(() => {
    if (cols <= 0) return new Set();
    return computeFilledSet(Math.max(0, total | 0), Math.max(0, filled | 0), cols);
  }, [total, filled, cols]);

  const visualCells = useMemo(() => {
    const items = [];
    for (let i = 0; i < Math.max(0, total | 0); i++) {
      items.push({ type: "real", idx: i });
    }
    if (cols > 0) {
      const lastRem = (Math.max(0, total | 0)) % cols;
      if (lastRem > 0) {
        const pad = Math.floor((cols - lastRem) / 2);
        if (pad > 0) {
          const start = Math.floor((Math.max(0, total | 0)) / cols) * cols;
          const padItems = Array.from({ length: pad }, (_, k) => ({ type: "spacer", key: `sp-${k}` }));
          items.splice(start, 0, ...padItems);
        }
      }
    }
    return items;
  }, [total, cols]);

  const cellSize = Math.max(1, cell);

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
    gridAutoRows: `${cellSize}px`,
    gap: `${gap}px`,
    width: "100%",
    height: "100%",
    placeContent: "center",
    background: colors.gridBg,
    minWidth: 0,
    minHeight: 0,
    boxSizing: "border-box",
  };

  const squareStyleBase = {
    borderRadius: rounded ? 'calc(2vh + 2vw)' : 0,
    boxShadow: `inset 0 0 0 0.5px ${colors.border}`,
    transition: "transform 250ms ease, background 250ms linear",
    willChange: "transform, background",
  };

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        width: "100%",
        height: style.height ?? "100%",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        boxSizing: "border-box",
        ...style,
      }}
    >
      <div style={gridStyle} aria-label="Dynamic waffle chart" role="img">
        {visualCells.map((item) => {
          if (item.type === "spacer") {
            return <div key={item.key} style={{ visibility: "hidden", boxShadow: "none" }} aria-hidden />;
          }
          const i = item.idx;
          const isFilled = filledSet.has(i);
          return (
            <div
              className="transition-all duration-300 hover:opacity-80"
              key={i}
              title={`slot ${i + 1}`}
              style={{
                ...squareStyleBase,
                opacity: isFilled ? 1 : 0.9,
                background: isFilled ? colors.filled : colors.empty,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
